import { isDummyPaymentTarget, silentPaymentAddressFrom } from './validateAddress';

export { isDummyPaymentTarget } from './validateAddress';

/** QR image URL. Google Charts Infographics is dead (404); use a public QR endpoint. */
export function getQrImageUrl(data: string, size = 300): string {
  const payload = data.trim();
  if (!payload || isDummyPaymentTarget(payload)) return '';
  const encoded = encodeURIComponent(payload);
  const px = Math.max(80, Math.min(size, 1000));
  return `https://api.qrserver.com/v1/create-qr-code/?size=${px}x${px}&data=${encoded}&ecc=M&margin=1`;
}

/** BIP-21 `bitcoin:` URI. `amount` is BTC (not sats). Empty if the address is dummy/missing. */
export function bitcoinQrData(address: string, amountSats?: number): string {
  const trimmed = address.trim();
  if (!trimmed || isDummyPaymentTarget(trimmed)) return '';
  if (amountSats != null && amountSats > 0) {
    const btc = amountSats / 100_000_000;
    const amount = btc.toFixed(8).replace(/\.?0+$/, '');
    return `bitcoin:${trimmed}?amount=${amount}`;
  }
  return `bitcoin:${trimmed}`;
}

/**
 * QR payload for a BIP352 silent payment address.
 * Wallets exchange the bare `sp1…` address, so the descriptor prefix is stripped
 * and no URI scheme is invented. Empty string when the descriptor is invalid.
 */
export function silentPaymentQrData(address: string): string {
  return silentPaymentAddressFrom(address) ?? '';
}

export function lightningQrData(invoice: string): string {
  const trimmed = invoice.trim();
  if (!trimmed || isDummyPaymentTarget(trimmed)) return '';
  if (
    trimmed.startsWith('lightning:') ||
    trimmed.startsWith('bitcoin:') ||
    trimmed.startsWith('lnurl:')
  ) {
    return trimmed;
  }
  return `lightning:${trimmed}`;
}

export function isBolt11Invoice(value: string): boolean {
  const v = value.trim().replace(/^lightning:/i, '');
  return /^(lnbc|lntbs|lntb|lnbcrt)/i.test(v);
}

export interface WalletChip {
  name: string;
  href: string;
  /** `uri` opens a wallet scheme; `web` is an invoice handler page (not a homepage). */
  kind: 'uri' | 'web';
}

/** Wallets that can actually consume `paymentUri`. Homepage-only chips (WoS, empty muun://) are omitted. */
export function buildWalletDeepLinks(paymentUri: string): WalletChip[] {
  const raw = paymentUri.trim();
  if (!raw) return [];

  const withScheme =
    raw.startsWith('lightning:') || raw.startsWith('bitcoin:') || raw.startsWith('lnurl:')
      ? raw
      : lightningQrData(raw);

  const bolt11 = withScheme.replace(/^lightning:/i, '');
  const chips: WalletChip[] = [];

  if (isBolt11Invoice(withScheme)) {
    chips.push({
      name: 'Phoenix',
      href: `https://phoenix.acinq.co/invoice?data=${encodeURIComponent(bolt11)}`,
      kind: 'web',
    });
    chips.push({
      name: 'Zeus',
      href: `zeusln://send?invoice=${encodeURIComponent(bolt11)}`,
      kind: 'uri',
    });
  }

  if (withScheme.startsWith('bitcoin:')) {
    chips.push({ name: 'Muun', href: withScheme, kind: 'uri' });
  }

  return chips;
}

export function defaultWalletHref(paymentUri: string): string {
  const raw = paymentUri.trim();
  if (!raw) return '';
  if (raw.startsWith('lightning:') || raw.startsWith('bitcoin:') || raw.startsWith('lnurl:')) return raw;
  return lightningQrData(raw);
}